
import { useEffect, useState } from 'react';
import { Alert, Box, Button, Grid, LinearProgress, Paper, Snackbar, SnackbarCloseReason, TextField } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { VTextField,  VForm, useVForm, IVFormErrors, BasicSelectUF } from '../../../shared/forms';
import { FerramentasDeDetalhe, TelaBloqueioSalvando } from '../../../shared/components';
import { useBloqueioSalvamento } from '../../../shared/hooks';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { SelectNumber } from '../../../shared/forms/Selectnumber';
import { ReboqueService } from '../../../shared/services/api/cadastros/ReboqueService';
import { Api } from '../../../shared/services/api/axiosApi';

interface IFormData {
  idtransportadora: string;
  placa: string;
  renavam: string;
  capkg: number; 
  capm3: number;
  tipocarroceria: number;
  uf: string;
  tara: number;
}

const formValidationSchema: yup.ObjectSchema<IFormData> = yup.object().shape({
  idtransportadora: yup.string().required(),
  placa: yup.string().required().min(7).max(7),
  renavam: yup.string().required().min(1).max(12),
  capkg: yup.number().required().moreThan(0),
  capm3: yup.number().required().moreThan(0),
  tipocarroceria: yup.number().required(),
  uf: yup.string().required().min(2).max(2),
  tara: yup.number().required().moreThan(0),
});
export const DetalheDeReboques: React.FC = () => {
  const { id = 'novo' } = useParams<'id'>();
  const { idTransportadora = 'novo' } = useParams<'idTransportadora'>();
  const navigate = useNavigate();
  const { formRef, save, saveAndClose } = useVForm();
  const [isLoading, setIsLoading] = useState(false);
  const { isSaving, progress: saveProgress, iniciar: iniciarSalvamento, concluir: concluirSalvamento, cancelar: cancelarSalvamento } = useBloqueioSalvamento();
  const [nomeTransportadora, setNomeTransportadora] = useState('');
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error'  | 'info'  | 'success'>('warning');  

  const [openConfirm, setOpenConfirm] = useState(false);

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event, 
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenmsg(false);
    if(tms === 'success'){ 
      navigate(`/reboques?idtransportadora=${idTransportadora}`);
    }
  };

  const handleDelete = () => {
    setOpenConfirm(true); 
  };
  const handleConfirmDelete = () => {
    ReboqueService.deleteById(id)
      .then(result => {
        if (result instanceof Error) {
          setOpenConfirm(false);
          setTms('error');
          setMsg(result.message);
          setOpenmsg(true);
        } else {
          setOpenConfirm(false);
          setTms('success');
          setMsg('Registro apagado com sucesso!');
          setOpenmsg(true);
        }
      });
  };
  const handleCancelDelete = () => {
    setOpenConfirm(false);
  };
  useEffect(() => {
    if (id !== 'novo') {
      setIsLoading(true);
      ReboqueService.getById(id)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setTms('error');
            setMsg(result.message);
            setOpenmsg(true);
          } else {
            formRef.current?.setData(result);
          }
        });
    } else {
      formRef.current?.setData({
        idtransportadora: {idTransportadora},
        placa: '',
        renavam: '',
        capkg: '',
        capm3: '',
        tipocarroceria: '',
        uf: '',
        tara: '',
      });
    }
  }, [id]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await Api.get(`/transportadoras/${idTransportadora}`);
        const transportadorasData = response.data;
        setNomeTransportadora(transportadorasData.nome);
      } catch (error) {
        setIsLoading(false);
        setTms('error');
        setMsg('Erro ao obter nome da Transportadora.');
        setOpenmsg(true);
      }
    };
  
    fetchData();
  }, [id, idTransportadora]);
  
  const handleSave = (dados: IFormData) => {
    if (!iniciarSalvamento()) return;

    const novosDados: IFormData = {
      ...dados,
      idtransportadora: idTransportadora,
    };
    formValidationSchema.
      validate(novosDados, { abortEarly: false })
      .then((dadosValidados) => {
        setIsLoading(true);
        if (id === 'novo') {
          ReboqueService
            .create(dadosValidados)
            .then((result) => {
              setIsLoading(false);
              if (result instanceof Error) {
                cancelarSalvamento();
                setTms('error');
                setMsg(result.message);
                setOpenmsg(true);
              } else {
                setTms('success');
                setMsg('Lançamento Realizado com sucesso!');
                setOpenmsg(true);
                concluirSalvamento();
              }
            });
        } else {
          ReboqueService
            .updateById(id, dadosValidados )
            .then((result) => {
              setIsLoading(false);
              if (result instanceof Error) {
                cancelarSalvamento();
                setTms('error');
                setMsg(result.message);
                setOpenmsg(true);
              } else {
                setTms('success');
                setMsg('Alteração concluída com sucesso!');
                setOpenmsg(true);
                concluirSalvamento();
              }
            });
        }
      })
      .catch((errors: yup.ValidationError) => {
        cancelarSalvamento();
        const validationErrors: IVFormErrors = {};
        errors.inner.forEach(error => {
          if (!error.path) return;
          validationErrors[error.path] = error.message;
        });
        formRef.current?.setErrors(validationErrors);
      });
  };

  return (
    <>
      <TelaBloqueioSalvando
        open={isSaving}
        message={id === 'novo' ? 'Salvando semirreboque...' : 'Salvando alterações...'}
        progress={saveProgress}
      />
      <LayoutBaseDePagina
      titulo={id === 'novo' ? 'Novo Semirreboque' : nomeTransportadora}
      barraDeFerramentas={
        <FerramentasDeDetalhe
          textoBotaoNovo='Novo'
          mostrarBotaoSalvarEFechar
          mostrarBotaoSalvarCarregando = {isLoading}
          mostrarBotaoSalvarEFecharCarregando = {isLoading}
          mostrarBotaoNovo={id !== 'novo'}
          mostrarBotaoApagar={id !== 'novo'}
          mostrarBotaoApagarCarregando = {isLoading}
          mostrarBotaoImpressao = {false}
          disableBotaoSalvar={isLoading || isSaving}

          aoClicarEmSalvar={save}
          aoClicarEmSalvarEFechar={saveAndClose}
          aoClicarEmVoltar={() => navigate(`/reboques?idtransportadora=${idTransportadora}`)}
          mostrarBotaoVoltarCarregando = {isLoading}
          aoClicarEmApagar={() => handleDelete()}
          aoClicarEmNovo={() => navigate(`/reboques/novo/${idTransportadora}`)}
          mostrarBotaoNovoCarregando = {isLoading}
        />
      }
    >

      <VForm ref={formRef} onSubmit={handleSave}placeholder={undefined} onPointerEnterCapture={undefined}onPointerLeaveCapture={undefined}>
        <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg}  autoHideDuration={900} onClose={handleCloseMsg}>
          <Alert
            onClose={handleCloseMsg}
            severity={tms}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {msg}
          </Alert>
        </Snackbar>;
        <Snackbar open={openConfirm} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert
            severity="warning"  
            variant="filled"
            sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
            action={
              <>
                <Button color="inherit" size="small" onClick={handleConfirmDelete}>
              Sim
                </Button>
                <Button color="inherit" size="small" onClick={handleCancelDelete}>
              Nâo
                </Button>
              </>
            }
          >
            Tem certeza que deseja excluir este registro?
          </Alert>
        </Snackbar>
        <Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined">
          <Grid container direction="column" padding={2} spacing={2}>
            {isLoading && (
              <Grid item>
                <LinearProgress variant='indeterminate' />
              </Grid>
            )}
            <Grid container item direction="row" spacing={2}>
              <Grid item xs={12} sm={12} md={6} lg={5} xl={4}>
                <TextField
                  size='small'
                  fullWidth
                  value={nomeTransportadora}
                  disabled={true}
                  label='Nome/Razão Social'
                />
              </Grid> 
              <Grid item xs={6} sm={6} md={3} lg={2} xl={2}>
                <VTextField
                  size='small'
                  fullWidth
                  name='placa'
                  disabled={isLoading}
                  label='Placa'
                />
              </Grid> 
              <Grid item xs={6} sm={6} md={3} lg={2} xl={2}>          
                <BasicSelectUF
                  name='uf'
                  disabled={isLoading}
                />
              </Grid>
              <Grid item xs={6} sm={6} md={3} lg={3} xl={2}>
                <VTextField
                  size='small'
                  fullWidth
                  name='renavam'
                  disabled={isLoading}
                  label='Renavam'
                />
              </Grid> 
              <Grid item xs={6} sm={6} md={3} lg={2.5} xl={2}>          
                <VTextField
                  size='small'
                  fullWidth
                  name='tara'
                  disabled={isLoading}
                  label='Tara'
                />
              </Grid> 
              <Grid item xs={6} sm={6} md={3} lg={2.5} xl={2}>          
                <VTextField
                  size='small'
                  fullWidth
                  name='capkg'
                  disabled={isLoading}
                  label='Capacidade Kg'
                />
              </Grid>
              <Grid item xs={6} sm={6} md={3} lg={2} xl={2}>          
                <VTextField
                  size='small'
                  fullWidth
                  name='capm3'
                  disabled={isLoading}
                  label='Capacidade M³'
                />
              </Grid>
              
              <Grid item xs={6} sm={12} md={6} lg={5} xl={2}>          
                <SelectNumber
                  data={[
                    { nameBD: 0, descricao: 'Não aplicável' },
                    { nameBD: 1, descricao: 'Aberta' },
                    { nameBD: 2, descricao: 'Fechada' },
                    { nameBD: 3, descricao: 'Graneleira' },
                    { nameBD: 4, descricao: 'Porta Contêiner' },
                    { nameBD: 5, descricao: 'Sider' },
                  ]}
                  label='Tipo de Carroceria'
                  name='tipocarroceria' 
                  disabled={isLoading}
                />
              </Grid>  
            </Grid>
          </Grid>
        </Box>
      </VForm>

      </LayoutBaseDePagina>
    </>
  );
};